<template>
  <TabPanel class="tw-w-full">
    <div class="tw-grid tw-grid-cols-12 tw-gap-5">
      <Box
        v-for="tile in tiles"
        :key="tile.key"
        rounded="xlarge"
        class="tw-relative tw-col-span-full tw-border tw-bg-white/5 tw-p-6 md:tw-col-span-6"
        :class="tile.running ? 'tw-border-primary' : 'tw-border-transparent'"
      >
        <div
          v-if="startingKey === tile.key"
          class="tw-absolute tw-inset-0 tw-z-10 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-rounded-2xl tw-backdrop-blur"
        >
          <Spinner size="large" />
          <Text>{{ spinnerText }}</Text>
        </div>

        <Tag v-if="tile.running" size="small" color="green" class="tw-absolute tw-right-5 tw-top-5"> Running </Tag>

        <div class="tw-space-y-3">
          <Icon :name="tile.icon" class="tw-size-8 tw-fill-white/89" />
          <Heading level="h3">{{ tile.title }}</Heading>

          <div v-if="tile.showDownloaded" class="tw-flex tw-items-center tw-gap-2">
            <Text class="tw-text-white/68">{{ t('launch_tiles.downloaded_version') }}</Text>
            <Tag size="small" color="gradient">
              {{ tile.downloadedVersion }}
            </Tag>
          </div>

          <div v-if="tile.showRunning" class="tw-flex tw-items-center tw-gap-2">
            <Text class="tw-text-white/68">{{ t('launch_tiles.running_version') }}</Text>
            <Tag size="small" color="gradient">
              {{ tile.runningVersion }}
            </Tag>
          </div>

          <div class="tw-flex tw-items-center tw-gap-1">
            <Icon :name="stateIcon(tile)" class="tw-size-6" :class="stateIconClass(tile)" />
            <Text>
              <template v-if="tile.state === 'no_version'">
                {{ t('launch_tiles.msg_no_version') }}
                <Link href="#" scheme="underline" @click.prevent="onCallSupportOpen = true">{{
                  t('launch_tiles.contact_support')
                }}</Link>
              </template>

              <template v-else-if="tile.state === 'up_to_date'">{{ t('launch_tiles.msg_up_to_date') }}</template>

              <template v-else-if="tile.state === 'downloading'">{{
                t('launch_tiles.msg_download_in_progress')
              }}</template>

              <template v-else-if="tile.state === 'ready'">{{ t('launch_tiles.msg_download_success') }}</template>

              <template v-else-if="tile.state === 'failed'">
                {{ t('launch_tiles.msg_error_before') }}
                <Link href="#" scheme="underline" :disabled="startingKey === tile.key" @click.prevent="download(tile)">
                  {{ t('launch_tiles.here') }}
                </Link>
                {{ t('launch_tiles.msg_error_after') }}
              </template>

              <template v-else-if="tile.state === 'available'">
                {{ t('launch_tiles.msg_available_before') }}
                <Link href="#" scheme="underline" :disabled="startingKey === tile.key" @click.prevent="download(tile)">
                  {{ t('launch_tiles.here') }}
                </Link>
                {{ t('launch_tiles.msg_available_after') }}
              </template>
            </Text>
          </div>
        </div>

        <div v-if="tile.state !== 'no_version'" class="tw-mt-5">
          <Button
            color="transparentWhite"
            outlined
            :disabled="tile.runDisabled || startingKey === tile.key"
            @click="run(tile)"
          >
            {{ t('launch_tiles.run') }}
          </Button>
        </div>
      </Box>
    </div>
  </TabPanel>

  <OnCallSupportModal v-model="onCallSupportOpen" />
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { Box, Button, Heading, Icon, Link, Spinner, TabPanel, Tag, Text } from '@slideslive/fuse-kit/vue';

import AlertCircleOutlineSvg from '@/assets/mdi/alert-circle-outline.svg';
import AlertOutlineSvg from '@/assets/mdi/alert-outline.svg';
import FlaskOutlineSvg from '@/assets/mdi/flask-outline.svg';
import RocketOutlineSvg from '@/assets/mdi/rocket-outline.svg';
import SuccessOutlineSvg from '@/assets/mdi/success-outline.svg';
import OnCallSupportModal from '@/components/OnCallSupportModal.vue';
import fetcher from '@/fetcher';
import useStateStore from '@/stores/state';
import { versionLabel } from '@/utils/sidebar_status';

const { t } = useI18n();

const stateStore = useStateStore();
const { state, wiringHasChanges, wiringValues, transfersUi } = storeToRefs(stateStore);

const channels = computed(() => state.value?.data?.channels?.production || {});
const nate = computed(() => state.value?.data?.nate || {});
const nateStatus = computed(() => (nate.value?.status || '').toLowerCase());
const transfers = computed(() => transfersUi.value || {});

const startingKey = ref(null);
const onCallSupportOpen = ref(false);

const spinnerText = computed(() => {
  if (!startingKey.value) return '';

  if (nateStatus.value === 'stopped' || nateStatus.value === 'not_running') {
    return 'Waiting for NATE to start…';
  }

  return 'Starting…';
});

function createdAtOf(img) {
  return String(img?.created_at || '').trim();
}

const runningImage = computed(() => nate.value?.image || null);

function sameImageByCreatedAt(local) {
  const localCreatedAt = createdAtOf(local);
  const runningCreatedAt = createdAtOf(runningImage.value);
  return Boolean(localCreatedAt && runningCreatedAt && localCreatedAt === runningCreatedAt);
}

function stateFrom({ transfer, local, remote }) {
  const tr = transfer || null;

  if (tr?.status === 'downloading') return 'downloading';
  if (tr?.status === 'failed') return 'failed';
  if (tr?.status === 'completed') return 'ready';

  const localCreatedAt = createdAtOf(local);
  const remoteCreatedAt = createdAtOf(remote);

  if (!localCreatedAt && !remoteCreatedAt) return 'no_version';
  if (remoteCreatedAt && (!localCreatedAt || localCreatedAt !== remoteCreatedAt)) return 'available';

  return 'up_to_date';
}

const channelConfig = {
  stable: { title: 'Stable', icon: RocketOutlineSvg },
  beta: { title: 'Beta', icon: FlaskOutlineSvg },
};

const tiles = computed(() => {
  return Object.entries(channelConfig)
    .map(([key, cfg]) => {
      const channel = channels.value?.[key];
      if (!channel) return null;

      const local = channel.local || null;
      const remote = channel.remote || null;

      const runningHere = Boolean(channel.is_running);
      const same = runningHere && sameImageByCreatedAt(local);

      const downloadedVersion = versionLabel(local);
      const runningVersion = runningHere ? versionLabel(runningImage.value) : '-';

      const transfer = transfers.value?.[key] || null;

      return {
        key,
        title: cfg.title,
        icon: cfg.icon,
        running: runningHere,

        downloadedVersion,
        runningVersion,

        showRunning: runningHere,
        showDownloaded: Boolean(local) && (!runningHere || !same),

        state: stateFrom({ transfer, local, remote }),
        runDisabled: transfer?.status === 'downloading',
      };
    })
    .filter(Boolean);
});

const stateIcon = (tile) => {
  if (tile.state === 'failed') return AlertCircleOutlineSvg;
  if (tile.state === 'available') return AlertOutlineSvg;
  if (tile.state === 'downloading') return AlertOutlineSvg;
  if (tile.state === 'no_version') return AlertCircleOutlineSvg;
  return SuccessOutlineSvg;
};

const stateIconClass = (tile) => (tile.state === 'up_to_date' ? 'tw-fill-state-succ' : 'tw-fill-state-warn');

const download = async (tile) => {
  if (startingKey.value === tile.key) return;

  await fetcher.post('/images/-/download', {
    tag: tile.key,
    use_dev_registry: false,
  });
};

const run = async (tile) => {
  if (startingKey.value) return;

  if (wiringHasChanges.value) {
    const ok = window.confirm('Run with changed wiring?');
    if (!ok) return;
  }

  startingKey.value = tile.key;

  try {
    const res = await fetcher.post('/nate/-/run', {
      tag: tile.key,
      use_dev_registry: false,
      wiring_values: wiringValues.value,
    });

    if (!res || res.success === false) {
      console.error('Run failed', res);
    }
  } catch (e) {
    console.error(e);
  } finally {
    startingKey.value = null;
  }
};
</script>
